/**
 * @description Client grain, dormant, callout, and modal math for TowTech
 *   SO Assets & Crew (LYN-408 / LYN-421 / LYN-425 / LYN-428 / LYN-430 / LYN-436 / LYN-438 / LYN-446).
 *
 * Key features:
 * - One row per Driver-role employee or truck assignment
 * - Measures sum stored Utilization windows in the Overview Time range
 * - Dormant is a current-view soft filter (15d driver / 30d truck)
 * - Show All | Top | Bottom filters the table; Rank by / How many / Ignore
 *   low volume live in the settings popover
 * - Percents and averages are derived
 *
 * @dependencies ./scope1Dashboard (windowForFilter, TIMELINE_LABELS, civil math)
 *
 * @notes
 * - Company view clocks companies.timezone. All clocks the viewer zone.
 * - Same name, overlapping windows: keep the shorter one. Do not sum overlaps.
 * - A stored window that overlaps the Time range counts for that range.
 *   A window that covers two ranges belongs to one. Drop only a window
 *   that does not overlap the range at all.
 * - A filter only draws that grain or finer. Hour rolls stored hours.
 *   Day rolls stored hours and days. Week rolls hours, days, or weeks.
 *   Month, Quarter, and Year keep month chunks plus the tail after
 *   covered_through. A day inside the watermark is not added. Do not
 *   sum hour and month that cover the same days.
 * - Missing Day / Week grain is a reason line on charts and lists.
 *   BI callouts omit the insight. Missing points are absent, not zero.
 * - A Time filter or modal Week/Month with no stored point is grey.
 * - Modal Week keeps week-or-finer. A month chunk is not drawn as a week.
 * - Modal Week / Month is last 4 complete plus Current. Current always
 *   plots. Month Current is the chunk plus the tail after covered_through.
 *   Week Current uses days and hours that start this week. Never a
 *   month chunk. A complete month or week with no stored grain stays
 *   omitted. No dollar +/-.
 * - Hour displays last 24 hours. Day is last 7 days and includes those 24.
 *   A multi-day lookback is not Last 24 hours. Missing grain is the
 *   collecting line. Do not substitute a different window and keep the label.
 * - Unassigned is a real truck row. Second company is a second assignment.
 * - Do not invent Expense:* keys. Do not write jobs.
 * - Revolving copy names the Time grain. Never say "window."
 * - Unpaid sums the same distinct periods as the table. A null
 *   totalBalanceCents on any picked period stays null (fail closed).
 *   updatedAt is the newest stamp among those picked periods.
 * - Month, quarter, and year omit the invoice percent. Hour, day,
 *   and week keep same-length timeGrainWords.
 */
import {
  MS_HOUR,
  addZonedDays,
  addZonedMonths,
  chartTimeZone,
  formatFilterBucketLabel,
  zonedDateKey,
  zonedDateParts,
  zonedDayStart,
  zonedMonthStart,
  zonedQuarterStart,
  zonedWeekStart,
  zonedYearStart
} from './scope1Dashboard';

export const DRIVER_DORMANT_DAYS = 15;
export const TRUCK_DORMANT_DAYS = 30;
export const MIN_CALL_COUNT_DEFAULT = 1;
export const RANK_PEERS_MIN = 10;
export const DEFAULT_BAND_PERCENTILE = 10;
export const MS_DAY = 24 * 60 * 60 * 1000;

export const REPORT_BY = Object.freeze({
  driver: 'driver',
  truck: 'truck'
});

export const MEASURES = Object.freeze([
  { key: 'callCount', label: 'Call Count', kind: 'count' },
  { key: 'totalInvoiceCents', label: 'Total Invoice', kind: 'cents' },
  { key: 'totalBalanceCents', label: 'Total Balance', kind: 'cents' },
  { key: 'totalCommissionsCents', label: 'Total Commissions', kind: 'cents' },
  { key: 'averageInvoicePerCallCents', label: 'Average Invoice Per Call', kind: 'cents' },
  { key: 'towingSubtotalsCents', label: 'Towing Subtotals', kind: 'cents' },
  { key: 'storageSubtotalsCents', label: 'Storage Subtotals', kind: 'cents' }
]);

export const BAND_METRICS = Object.freeze(
  MEASURES.filter((measure) => measure.key !== 'averageInvoicePerCallCents')
);

export const SHOW = Object.freeze({
  all: 'all',
  top: 'top',
  bottom: 'bottom'
});

export const RANK_BY = Object.freeze([
  { key: 'totalInvoiceCents', label: 'Total Invoice' },
  { key: 'callCount', label: 'Call Count' },
  { key: 'averageInvoicePerCallCents', label: 'Average Invoice' }
]);

export const HOW_MANY = Object.freeze([
  { key: 'top10', label: 'Top 10%', direction: 'top', kind: 'pct', n: 10 },
  { key: 'top5', label: 'Top 5', direction: 'top', kind: 'count', n: 5 },
  { key: 'bottom10', label: 'Bottom 10%', direction: 'bottom', kind: 'pct', n: 10 },
  { key: 'bottom5', label: 'Bottom 5', direction: 'bottom', kind: 'count', n: 5 }
]);

export const MODAL_RANGE = Object.freeze({
  weeks4: 'weeks4',
  months4: 'months4'
});

export const PRIMARY_MEASURES = Object.freeze([
  { key: 'callCount', label: 'Call Count', kind: 'count' },
  { key: 'totalInvoiceCents', label: 'Total Invoice', kind: 'cents' },
  { key: 'totalBalanceCents', label: 'Total Balance', kind: 'cents' },
  { key: 'averageInvoicePerCallCents', label: 'Average Invoice', kind: 'cents' }
]);

export const SECONDARY_MEASURES = Object.freeze([
  { key: 'totalCommissionsCents', label: 'Total Commissions', kind: 'cents' },
  { key: 'towingSubtotalsCents', label: 'Towing Subtotals', kind: 'cents' },
  { key: 'storageSubtotalsCents', label: 'Storage Subtotals', kind: 'cents' }
]);

export const EMPTY_WINDOW = 'No Utilization data for this window.';
export const FAILED_WINDOW = "Couldn't load Utilization data.";
export const MISSING_HOUR_GRAIN = 'Not enough last-24-hours data yet. This view will populate as additional data is collected.';
export const MISSING_DAY_GRAIN = 'Not enough per-day data yet. This view will populate as additional data is collected.';
export const MISSING_WEEK_GRAIN = 'Not enough per-week data yet. This view will populate as additional data is collected.';
export const PARTIAL_GRAIN = 'Partial, still collecting.';
export const DATA_WHEN_AVAILABLE = 'Data will display when available.';
export const REVOLVE_MS = 7000;
export const HOUR_LOCK_MS = 26 * 60 * 60 * 1000;
export const STORED_HOUR_MS = 2.5 * 60 * 60 * 1000;

const ZERO_MEASURES = Object.freeze({
  callCount: 0,
  totalInvoiceCents: 0,
  totalBalanceCents: 0,
  totalCommissionsCents: 0,
  averageInvoicePerCallCents: 0,
  towingSubtotalsCents: 0,
  storageSubtotalsCents: 0,
  totalExpensesCents: 0,
  expenses: Object.freeze({}),
  updatedAt: null
});

const MONTH_SHORT = Object.freeze([
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
]);

/**
 * @param {unknown} value
 * @returns {string}
 */
export function trimCell(value) {
  return value == null ? '' : String(value).trim();
}

/**
 * Person-shaped Utilization driver Name. Non-person labels return null.
 * @param {unknown} rawName
 * @returns {{ firstName: string, lastName: string|null, driverNumber: string }|null}
 */
export function parseDriverName(rawName) {
  const name = trimCell(rawName);
  if (!name) return null;
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return null;
  const trailing = parts[parts.length - 1];
  if (!/^\d+$/.test(trailing)) return null;
  const rest = parts.slice(0, -1);
  if (rest.length === 0) return null;
  if (rest.length === 1) {
    return { firstName: rest[0], lastName: null, driverNumber: trailing };
  }
  return {
    firstName: rest.slice(0, -1).join(' '),
    lastName: rest[rest.length - 1],
    driverNumber: trailing
  };
}

/**
 * Truck Name. Unassigned is a real truck with no number.
 * @param {unknown} rawName
 * @returns {{ truckNumber: string|null, unassigned: boolean }|null}
 */
export function parseTruckName(rawName) {
  const name = trimCell(rawName);
  if (!name) return null;
  if (name.toLowerCase() === 'unassigned') {
    return { truckNumber: null, unassigned: true };
  }
  return { truckNumber: name, unassigned: false };
}

/**
 * @param {{ firstName?: string|null, lastName?: string|null, driverNumber?: string|null }} employee
 * @returns {string}
 */
export function displayDriverName(employee) {
  return [employee?.firstName, employee?.lastName, employee?.driverNumber]
    .map((part) => trimCell(part))
    .filter(Boolean)
    .join(' ');
}

/**
 * @param {{ truckNumber?: string|null }} truck
 * @returns {string}
 */
export function displayTruckName(truck) {
  return truck?.truckNumber == null || truck.truckNumber === ''
    ? 'Unassigned'
    : String(truck.truckNumber);
}

/**
 * @param {object} window
 * @param {object} employee
 * @returns {boolean}
 */
export function matchDriverWindow(window, employee) {
  if (!window || window.reportBy !== 'driver') return false;
  const parsed = parseDriverName(window.name);
  if (parsed && employee?.driverNumber != null) {
    return parsed.driverNumber === String(employee.driverNumber);
  }
  return trimCell(window.name) === displayDriverName(employee);
}

/**
 * @param {object} window
 * @param {object} truck
 * @returns {boolean}
 */
export function matchTruckWindow(window, truck) {
  if (!window || window.reportBy !== 'truck') return false;
  const parsed = parseTruckName(window.name);
  if (!parsed) return false;
  if (parsed.unassigned) return truck?.truckNumber == null;
  return truck?.truckNumber != null && String(truck.truckNumber) === parsed.truckNumber;
}

/**
 * @param {object} window
 * @param {{ from: Date, to: Date }} range
 * @returns {boolean}
 */
export function windowOverlapsRange(window, range) {
  if (!window || !range) return false;
  const start = new Date(window.windowStart).getTime();
  const end = new Date(window.windowEnd).getTime();
  const bounds = asRange(range);
  if (!Number.isFinite(start) || !Number.isFinite(end) || !bounds) return false;
  return start < bounds.to.getTime() && end > bounds.from.getTime();
}

/**
 * @param {{ from: Date|string, to: Date|string }} range
 * @returns {{ from: Date, to: Date }|null}
 */
function asRange(range) {
  if (!range) return null;
  const from = range.from?.getTime?.() ?? new Date(range.from).getTime();
  const to = range.to?.getTime?.() ?? new Date(range.to).getTime();
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  return { from: new Date(from), to: new Date(to) };
}

/**
 * windowStart in [from, to). Used when two ranges both overlap.
 * @param {object} window
 * @param {{ from: Date|string, to: Date|string }} range
 * @returns {boolean}
 */
export function windowBelongsToRange(window, range) {
  if (!window || !range) return false;
  const start = new Date(window.windowStart).getTime();
  const bounds = asRange(range);
  if (!Number.isFinite(start) || !bounds) return false;
  return start >= bounds.from.getTime() && start < bounds.to.getTime();
}

/**
 * Time-range membership. Overlap counts. A window that covers this
 * range and a sibling still belongs to one — the later range, so an
 * hourly lookback that covers now counts for Hour, not prior.
 * Same-end tie uses windowStart. Drop only no-overlap.
 * @param {object} window
 * @param {{ from: Date|string, to: Date|string }} range
 * @param {{ from: Date|string, to: Date|string }|null} [sibling]
 * @returns {boolean}
 */
export function windowCountsForRange(window, range, sibling) {
  const here = asRange(range);
  if (!here || !windowOverlapsRange(window, here)) return false;
  const other = sibling ? asRange(sibling) : null;
  if (!other || !windowOverlapsRange(window, other)) return true;
  const hereTo = here.to.getTime();
  const otherTo = other.to.getTime();
  if (hereTo !== otherTo) return hereTo > otherTo;
  return windowBelongsToRange(window, here);
}

/**
 * @param {unknown} value
 * @returns {number}
 */
function asNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

const GRAIN_RANK = Object.freeze({
  hour: 0,
  day: 1,
  week: 2,
  month: 3,
  quarter: 4,
  year: 5
});

/**
 * @param {object} window
 * @returns {number}
 */
export function windowSpanMs(window) {
  const start = new Date(window?.windowStart).getTime();
  const end = new Date(window?.windowEnd).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return end - start;
}

/**
 * Infer stored pull grain from the window span. A closed hour is about
 * one hour. Catch-up chunks are calendar months. Do not split a coarse
 * chunk. A leftover multi-day lookback is not a stored hour.
 * @param {object} window
 * @returns {'hour'|'day'|'week'|'month'|'quarter'|'year'}
 */
export function storedWindowGrain(window) {
  const span = windowSpanMs(window);
  const days = span / MS_DAY;
  if (span > 0 && span <= STORED_HOUR_MS) return 'hour';
  if (days > 0 && days <= 1.5) return 'day';
  if (days <= 4) return 'hour';
  if (days <= 10) return 'week';
  if (days <= 40) return 'month';
  if (days <= 120) return 'quarter';
  return 'year';
}

/**
 * Closed stored hour. A leftover multi-day lookback is not.
 * @param {object} window
 * @returns {boolean}
 */
export function isStoredHourPoint(window) {
  return storedWindowGrain(window) === 'hour' && windowSpanMs(window) <= HOUR_LOCK_MS;
}

const FILTER_GRAINS = Object.freeze({
  hour: Object.freeze(['hour']),
  day: Object.freeze(['hour', 'day']),
  week: Object.freeze(['hour', 'day', 'week']),
  month: Object.freeze(['month']),
  quarter: Object.freeze(['month', 'quarter']),
  year: Object.freeze(['month', 'quarter', 'year'])
});

/**
 * A filter only draws windows at that grain or finer. Hour rolls stored
 * hours. A leftover lookback is not Hour, Day, or Week. Month keeps
 * the month chunk.
 * @param {object} window
 * @param {string} [filter]
 * @returns {boolean}
 */
export function windowFitsFilter(window, filter) {
  if (!filter || GRAIN_RANK[filter] == null) return true;
  const grain = storedWindowGrain(window);
  const hourPoint = isStoredHourPoint(window);
  if (filter === 'hour') return hourPoint;
  if (filter === 'day') return grain === 'day' || hourPoint;
  if (filter === 'week') return grain === 'day' || grain === 'week' || hourPoint;
  const allowed = FILTER_GRAINS[filter];
  if (!allowed) return GRAIN_RANK[grain] <= GRAIN_RANK[filter];
  return allowed.includes(grain);
}

/**
 * Locked display range. Hour is last 24 hours, not a multi-day lookback.
 * Day includes a true last-24-hours window. Do not substitute a wider
 * window and keep the label.
 * @param {object} window
 * @param {string} [filter]
 * @returns {boolean}
 */
export function windowFitsLockedRange(window, filter) {
  if (!filter || GRAIN_RANK[filter] == null) return true;
  if (filter === 'hour') {
    return windowSpanMs(window) > 0 && windowSpanMs(window) <= HOUR_LOCK_MS;
  }
  if (filter === 'day') {
    if (storedWindowGrain(window) === 'day') return true;
    return windowFitsLockedRange(window, 'hour');
  }
  return windowFitsFilter(window, filter);
}

/**
 * Hour belongs to last 24 hours by windowStart. Overlap would let a
 * multi-day lookback keep the Last 24 hours label.
 * @param {object} window
 * @param {{ from: Date|string, to: Date|string }} range
 * @param {string} [filter]
 * @param {{ from: Date|string, to: Date|string }|null} [sibling]
 * @returns {boolean}
 */
export function windowInLockedRange(window, range, filter, sibling) {
  if (!windowFitsLockedRange(window, filter)) return false;
  if (filter === 'hour') return windowBelongsToRange(window, range);
  return windowCountsForRange(window, range, sibling);
}

/**
 * Civil hour key used by both expected Hour count and stored coverage.
 * DST fall-back maps two UTC hours onto one zoned clock hour.
 *
 * @param {Date|string} value
 * @param {string} [timeZone]
 * @returns {string|null}
 */
export function hourCoverageKey(value, timeZone) {
  const parts = zonedDateParts(value, timeZone);
  if (!Number.isFinite(parts.year)) return null;
  return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}T${String(parts.hour).padStart(2, '0')}`;
}

/**
 * @param {string} filter
 * @param {{ from: Date, to: Date }} range
 * @param {string} [timeZone]
 * @returns {number}
 */
export function expectedCoverageSize(filter, range, timeZone) {
  const bounds = asRange(range);
  if (!bounds) return 0;
  if (filter === 'hour') {
    const keys = new Set();
    let hourCursor = bounds.from;
    let hourSteps = 0;
    while (hourCursor < bounds.to) {
      const key = hourCoverageKey(hourCursor, timeZone);
      if (key) keys.add(key);
      hourCursor = new Date(hourCursor.getTime() + MS_HOUR);
      hourSteps += 1;
      if (hourSteps > 48) break;
    }
    return keys.size;
  }
  let cursor;
  let count = 0;
  if (filter === 'day') {
    cursor = bounds.from;
    while (cursor < bounds.to) {
      count += 1;
      cursor = addZonedDays(cursor, 1, timeZone);
      if (count > 400) break;
    }
    return count;
  }
  if (filter === 'week') {
    cursor = zonedWeekStart(bounds.from, timeZone);
    while (cursor < bounds.to) {
      count += 1;
      cursor = addZonedDays(cursor, 7, timeZone);
      if (count > 80) break;
    }
    return count;
  }
  if (filter === 'month' || filter === 'quarter' || filter === 'year') {
    cursor = zonedMonthStart(bounds.from, timeZone);
    while (cursor < bounds.to) {
      count += 1;
      cursor = addZonedMonths(cursor, 1, timeZone);
      if (count > 24) break;
    }
    return count;
  }
  return 0;
}

/**
 * @param {object[]} windows
 * @param {string} filter
 * @param {string} [timeZone]
 * @returns {Set<string>}
 */
export function coverageKeys(windows, filter, timeZone) {
  const keys = new Set();
  (windows || []).forEach((window) => {
    if (filter === 'hour') {
      const key = hourCoverageKey(window.windowStart, timeZone);
      if (key) keys.add(key);
      return;
    }
    if (filter === 'day') {
      const key = zonedDateKey(window.windowStart, timeZone);
      if (key) keys.add(key);
      return;
    }
    if (filter === 'week') {
      const start = zonedWeekStart(window.windowStart, timeZone);
      if (start) keys.add(start.toISOString());
      return;
    }
    const start = zonedMonthStart(window.windowStart, timeZone);
    if (start) keys.add(start.toISOString());
  });
  return keys;
}

/**
 * Invoice comparisons print only when every expected slot is stored.
 *
 * @param {object[]} windows
 * @param {string} filter
 * @param {{ from: Date, to: Date }} range
 * @param {string} [timeZone]
 * @returns {{ present: number, expected: number, full: boolean }}
 */
export function grainCoverage(windows, filter, range, timeZone) {
  const fitting = (windows || []).filter((window) => windowInLockedRange(window, range, filter));
  const expected = expectedCoverageSize(filter, range, timeZone);
  const present = coverageKeys(fitting, filter, timeZone).size;
  return { present, expected, full: expected > 0 && present >= expected };
}

/**
 * Company ids that contribute at least one in-range window.
 * Windows with no companyId are omitted here; grainFullyCovered
 * treats those windows as not covered.
 *
 * @param {object[]} windows
 * @param {string} filter
 * @param {Array<{ from: Date, to: Date }|null|undefined>} ranges
 * @returns {string[]}
 */
export function coverageCompanyIds(windows, filter, ranges) {
  const ids = new Set();
  for (const range of ranges || []) {
    if (!range) continue;
    for (const window of windows || []) {
      if (!windowInLockedRange(window, range, filter)) continue;
      if (window.companyId == null || window.companyId === '') continue;
      ids.add(window.companyId);
    }
  }
  return [...ids];
}

/**
 * True only when every contributing company is full at this grain.
 * Pass { companyIds } as the union across current and prior so a
 * company that appears in only one range cannot pass the other.
 * A window with no companyId is not covered. grainCoverage still
 * reports the union of civil slots.
 *
 * @param {object[]} windows
 * @param {string} filter
 * @param {{ from: Date, to: Date }} range
 * @param {string} [timeZone]
 * @param {{ companyIds?: string[] }} [opts]
 * @returns {boolean}
 */
export function grainFullyCovered(windows, filter, range, timeZone, opts = {}) {
  const fitting = (windows || []).filter((window) => windowInLockedRange(window, range, filter));
  if (fitting.some((window) => window.companyId == null || window.companyId === '')) {
    return false;
  }
  const expected = expectedCoverageSize(filter, range, timeZone);
  if (expected <= 0) return false;
  const ids = Array.isArray(opts.companyIds) && opts.companyIds.length > 0
    ? opts.companyIds
    : [...new Set(fitting.map((window) => window.companyId))];
  if (ids.length === 0) return false;
  return ids.every((companyId) => (
    coverageKeys(
      fitting.filter((window) => window.companyId === companyId),
      filter,
      timeZone
    ).size >= expected
  ));
}

/**
 * Empty = no Utilization at all. Missing = finer grain not stored yet.
 * Partial = some of the grain. Hour counts its expected hourly slots
 * the same way Day counts days. A multi-day lookback is not Hour.
 * @param {{
 *   allWindows?: object[],
 *   grainWindows?: object[],
 *   filter?: string,
 *   range?: { from: Date, to: Date },
 *   timeZone?: string
 * }} input
 * @returns {{ kind: 'empty'|'missing'|'partial'|'full', reason: string|null, note: string|null }}
 */
export function grainViewState(input = {}) {
  const all = input.allWindows || [];
  const grainWindows = input.grainWindows || all;
  if (all.length === 0) {
    return { kind: 'empty', reason: null, note: null };
  }
  const fitting = grainWindows.filter((window) => (
    windowInLockedRange(window, input.range, input.filter)
  ));
  if (fitting.length === 0) {
    if (input.filter === 'hour') {
      return { kind: 'missing', reason: MISSING_HOUR_GRAIN, note: null };
    }
    if (input.filter === 'day') {
      return { kind: 'missing', reason: MISSING_DAY_GRAIN, note: null };
    }
    if (input.filter === 'week') {
      return { kind: 'missing', reason: MISSING_WEEK_GRAIN, note: null };
    }
    return { kind: 'empty', reason: null, note: null };
  }
  if (input.filter === 'hour') {
    const hasHour = fitting.some((window) => isStoredHourPoint(window));
    if (!hasHour) {
      return { kind: 'missing', reason: MISSING_HOUR_GRAIN, note: null };
    }
  }
  if (input.filter === 'day') {
    const hasDay = fitting.some((window) => storedWindowGrain(window) === 'day');
    const hasHour = fitting.some((window) => isStoredHourPoint(window));
    if (!hasDay && !hasHour) {
      return { kind: 'missing', reason: MISSING_DAY_GRAIN, note: null };
    }
  }
  const expected = expectedCoverageSize(input.filter, input.range, input.timeZone);
  const present = coverageKeys(fitting, input.filter, input.timeZone).size;
  if (expected > 0 && present < expected) {
    return { kind: 'partial', reason: null, note: PARTIAL_GRAIN };
  }
  return { kind: 'full', reason: null, note: null };
}

/**
 * Time filter is clickable only when a stored point fits the locked range.
 * @param {object[]} windows
 * @param {string} filter
 * @param {{ from: Date, to: Date }} range
 * @returns {boolean}
 */
export function timeFilterHasStoredPoint(windows, filter, range, opts = {}) {
  if (filter === 'month' || filter === 'quarter' || filter === 'year' || filter === 'week') {
    return selectCoveredWindows(windows, range, {
      filter,
      timeZone: opts.timeZone,
      now: opts.now
    }).length > 0;
  }
  return (windows || []).some((window) => windowInLockedRange(window, range, filter));
}

/**
 * Modal Week / Month is clickable only when a stored point would plot.
 * @param {object[]} windows
 * @param {'weeks4'|'months4'} modalFilter
 * @param {Date} now
 * @param {string} [timeZone]
 * @returns {boolean}
 */
export function modalFilterHasStoredPoint(windows, modalFilter, now, timeZone) {
  if (!windows || !windows.length) return false;
  return historyInRange(
    { windows },
    modalTimeRange(modalFilter, now, timeZone),
    timeZone,
    'invoice',
    { modalFilter, now }
  ).some((point) => (point.windows || []).length > 0);
}

/**
 * Overlapping same-name windows are not summed. Prefer the shorter window.
 * @param {object[]} windows
 * @returns {object[]}
 */
export function preferShorterWindows(windows) {
  const list = (Array.isArray(windows) ? windows : []).filter(Boolean);
  return [...list].sort((left, right) => {
    const spanDelta = windowSpanMs(left) - windowSpanMs(right);
    if (spanDelta !== 0) return spanDelta;
    const endDelta = new Date(right.windowEnd).getTime() - new Date(left.windowEnd).getTime();
    if (endDelta !== 0) return endDelta;
    const startDelta = new Date(right.windowStart).getTime() - new Date(left.windowStart).getTime();
    if (startDelta !== 0) return startDelta;
    return String(right.id || '').localeCompare(String(left.id || ''));
  });
}

/**
 * Preferred single window from a cluster. Overlaps are not summed here.
 * @param {object[]} windows
 * @returns {object|null}
 */
export function selectGrainWindow(windows) {
  const list = preferShorterWindows(windows);
  return list[0] || null;
}

/**
 * One window's measures. Average is derived.
 * @param {object} window
 * @returns {typeof ZERO_MEASURES & { expenses: Record<string, number> }}
 */
function measuresFromWindow(window) {
  if (!window) {
    return { ...ZERO_MEASURES, expenses: {} };
  }
  const extras = window.expenses && typeof window.expenses === 'object'
    ? window.expenses
    : {};
  const expenses = {};
  Object.keys(extras).forEach((key) => {
    if (!key.startsWith('Expense:')) return;
    expenses[key] = asNumber(extras[key]);
  });
  const callCount = asNumber(window.callCount);
  const totalInvoiceCents = asNumber(window.totalInvoiceCents);
  return {
    callCount,
    totalInvoiceCents,
    totalBalanceCents: window.totalBalanceCents === null
      ? null
      : asNumber(window.totalBalanceCents),
    totalCommissionsCents: asNumber(window.totalCommissionsCents),
    averageInvoicePerCallCents: callCount > 0
      ? Math.round(totalInvoiceCents / callCount)
      : 0,
    towingSubtotalsCents: asNumber(window.towingSubtotalsCents),
    storageSubtotalsCents: asNumber(window.storageSubtotalsCents),
    totalExpensesCents: asNumber(window.totalExpensesCents),
    expenses,
    updatedAt: window.updatedAt || null
  };
}

/**
 * Newer of two window stamps. Invalid stamps drop out.
 * @param {unknown} left
 * @param {unknown} right
 * @returns {unknown}
 */
function newerStamp(left, right) {
  const leftMs = left == null ? NaN : new Date(left).getTime();
  const rightMs = right == null ? NaN : new Date(right).getTime();
  if (!Number.isFinite(leftMs)) return Number.isFinite(rightMs) ? right : null;
  if (!Number.isFinite(rightMs)) return left;
  return rightMs >= leftMs ? right : left;
}

/**
 * Sum the non-overlapping windows. Empty list is zeros.
 * @param {object[]} windows
 * @param {string} [filter]
 * @returns {typeof ZERO_MEASURES & { expenses: Record<string, number> }}
 */
export function aggregateWindows(windows, filter) {
  const usable = (Array.isArray(windows) ? windows : []).filter((window) => (
    window && windowFitsLockedRange(window, filter)
  ));
  return sumMeasures(usable);
}

/**
 * @param {object[]} windows
 * @returns {string[]}
 */
export function presentExpenseKeys(windows) {
  const keys = new Set();
  (windows || []).forEach((window) => {
    const extras = window?.expenses && typeof window.expenses === 'object'
      ? window.expenses
      : {};
    Object.keys(extras).forEach((key) => {
      if (key.startsWith('Expense:')) keys.add(key);
    });
  });
  return [...keys].sort();
}

/**
 * @param {object} row
 * @param {Date} now
 * @param {number} days
 * @returns {boolean}
 */
export function isDormant(row, now, days) {
  const cutoff = now.getTime() - (days * MS_DAY);
  return !(row?.windows || []).some((window) => {
    const end = new Date(window.windowEnd).getTime();
    return Number.isFinite(end) && end >= cutoff && asNumber(window.callCount) > 0;
  });
}

/**
 * @param {object} payload
 * @param {{
 *   reportBy: 'driver'|'truck',
 *   range: { from: Date, to: Date },
 *   filter?: string,
 *   now?: Date,
 *   companyName?: string|null
 * }} opts
 * List rows sum the windows selectCoveredWindows already picked.
 * Do not pass filter into aggregateWindows here — a month tail is
 * hours and days, and a grain re-filter drops them.
 * @returns {{ empty: boolean, rows: object[], reason: string|null, partial: boolean }}
 */
export function buildGrainRows(payload, opts) {
  const windows = payload?.windows || [];
  const reportBy = opts.reportBy;
  const range = opts.range;
  const companyId = payload?.companyId;
  const companyName = opts.companyName || null;
  const view = grainViewState({
    allWindows: windows,
    grainWindows: windows.filter((window) => window.reportBy === reportBy),
    filter: opts.filter,
    range,
    timeZone: opts.timeZone
  });
  const current = selectCoveredWindows(
    windows.filter((window) => window.reportBy === reportBy),
    range,
    {
      filter: opts.filter,
      timeZone: opts.timeZone,
      now: opts.now
    }
  );
  if (current.length === 0) {
    return {
      empty: true,
      rows: [],
      reason: view.reason,
      partial: false
    };
  }

  const used = new Set();
  const rows = [];

  if (reportBy === 'driver') {
    (payload.drivers || []).forEach((employee) => {
      const matched = current.filter((window) => matchDriverWindow(window, employee));
      matched.forEach((window) => used.add(window.id));
      const history = windows.filter((window) => (
        window.reportBy === 'driver' && matchDriverWindow(window, employee)
      ));
      rows.push({
        id: `driver:${employee.employeeId}:${companyId}`,
        kind: 'driver',
        name: displayDriverName(employee),
        unassigned: false,
        companyId,
        companyName,
        subjectId: employee.employeeId,
        peerKey: employee.driverNumber != null ? `driver:${employee.driverNumber}` : `driver-name:${displayDriverName(employee)}`,
        createdAt: employee.employeeCreatedAt || null,
        measures: aggregateWindows(matched),
        windows: history
      });
    });
    const seenDriverNames = new Set();
    current.forEach((window) => {
      if (used.has(window.id)) return;
      const nameKey = trimCell(window.name);
      if (seenDriverNames.has(nameKey)) return;
      seenDriverNames.add(nameKey);
      const history = windows.filter((row) => (
        row.reportBy === 'driver' && trimCell(row.name) === nameKey
      ));
      const parsed = parseDriverName(window.name);
      rows.push({
        id: `driver-window:${window.name}:${companyId}`,
        kind: 'driver',
        name: window.name,
        unassigned: false,
        companyId,
        companyName,
        subjectId: null,
        peerKey: parsed ? `driver:${parsed.driverNumber}` : `driver-name:${window.name}`,
        createdAt: null,
        measures: aggregateWindows(
          current.filter((row) => trimCell(row.name) === nameKey)
        ),
        windows: history
      });
    });
  } else {
    (payload.trucks || []).forEach((truck) => {
      const matched = current.filter((window) => matchTruckWindow(window, truck));
      matched.forEach((window) => used.add(window.id));
      const history = windows.filter((window) => (
        window.reportBy === 'truck' && matchTruckWindow(window, truck)
      ));
      rows.push({
        id: `truck:${truck.truckId}:${companyId}`,
        kind: 'truck',
        name: displayTruckName(truck),
        unassigned: truck.truckNumber == null,
        companyId,
        companyName,
        subjectId: truck.truckId,
        peerKey: truck.truckNumber == null ? `truck:unassigned:${companyId}` : `truck:${truck.truckNumber}`,
        createdAt: truck.createdAt || null,
        measures: aggregateWindows(matched),
        windows: history
      });
    });
    const seenTruckNames = new Set();
    current.forEach((window) => {
      if (used.has(window.id)) return;
      const nameKey = trimCell(window.name);
      if (seenTruckNames.has(nameKey)) return;
      seenTruckNames.add(nameKey);
      const parsed = parseTruckName(window.name);
      const history = windows.filter((row) => (
        row.reportBy === 'truck' && trimCell(row.name) === nameKey
      ));
      rows.push({
        id: `truck-window:${window.name}:${companyId}`,
        kind: 'truck',
        name: parsed?.unassigned ? 'Unassigned' : window.name,
        unassigned: Boolean(parsed?.unassigned),
        companyId,
        companyName,
        subjectId: null,
        peerKey: parsed?.unassigned
          ? `truck:unassigned:${companyId}`
          : `truck:${window.name}`,
        createdAt: null,
        measures: aggregateWindows(
          current.filter((row) => trimCell(row.name) === nameKey)
        ),
        windows: history
      });
    });
  }

  return {
    empty: false,
    rows,
    reason: view.note,
    partial: view.kind === 'partial'
  };
}

/**
 * @param {Array<{ empty?: boolean, rows?: object[], reason?: string|null, partial?: boolean }>} grains
 * @returns {{ empty: boolean, rows: object[], reason: string|null, partial: boolean }}
 */
export function mergeGrains(grains) {
  const list = grains || [];
  const withRows = list.filter((grain) => grain && grain.empty === false);
  const missingReason = list.find((grain) => grain?.empty && grain?.reason)?.reason || null;
  const anyShort = list.some((grain) => (
    grain && (grain.partial || (grain.empty && grain.reason))
  ));
  if (withRows.length === 0) {
    const reason = missingReason || list.find((grain) => grain?.reason)?.reason || null;
    return { empty: true, rows: [], reason, partial: false };
  }
  return {
    empty: false,
    rows: withRows.flatMap((grain) => grain.rows || []),
    reason: missingReason || (anyShort ? PARTIAL_GRAIN : null),
    partial: anyShort
  };
}

/**
 * @param {object[]} rows
 * @param {Date} now
 * @param {'driver'|'truck'} reportBy
 * @returns {object[]}
 */
export function applyDormantFilter(rows, now, reportBy) {
  const days = reportBy === 'truck' ? TRUCK_DORMANT_DAYS : DRIVER_DORMANT_DAYS;
  return (rows || []).filter((row) => !isDormant(row, now, days));
}

/**
 * @param {object[]} rows
 * @returns {object[]}
 */
export function sortByTotalInvoiceDesc(rows) {
  return [...(rows || [])].sort((left, right) => {
    const delta = asNumber(right.measures?.totalInvoiceCents)
      - asNumber(left.measures?.totalInvoiceCents);
    if (delta !== 0) return delta;
    return String(left.name || '').localeCompare(String(right.name || ''));
  });
}

/**
 * @param {object[]} rows
 * @returns {object[]}
 */
export function topThreeByInvoice(rows) {
  return sortByTotalInvoiceDesc(rows).slice(0, 3);
}

/**
 * Median Call Count among rows that have at least one call.
 * @param {object[]} rows
 * @returns {number|null}
 */
export function medianCalls(rows) {
  const counts = (rows || [])
    .map((row) => asNumber(row.measures?.callCount))
    .filter((count) => count >= 1)
    .sort((left, right) => left - right);
  if (counts.length === 0) return null;
  const mid = Math.floor(counts.length / 2);
  if (counts.length % 2 === 0) return (counts[mid - 1] + counts[mid]) / 2;
  return counts[mid];
}

/**
 * Half the median calls, never below 1. Not a fixed minimum.
 * @param {object[]} rows
 * @returns {number|null}
 */
export function yieldFloor(rows) {
  const median = medianCalls(rows);
  if (median == null) return null;
  return Math.max(1, median / 2);
}

/**
 * @param {object} row
 * @returns {number}
 */
function yieldAverageCents(row) {
  const calls = asNumber(row?.measures?.callCount);
  if (calls <= 0) return 0;
  return asNumber(row.measures?.totalInvoiceCents) / calls;
}

/**
 * Top 3 eligible rows by invoice per call. Ties break on Total Invoice,
 * then Call Count. Drivers and trucks are ranked from the tab's own rows.
 * @param {object[]} rows
 * @returns {object[]}
 */
export function highestYield(rows) {
  const floor = yieldFloor(rows);
  if (floor == null) return [];
  const eligible = (rows || []).filter((row) => asNumber(row.measures?.callCount) >= floor);
  return [...eligible].sort((left, right) => {
    const avgDelta = yieldAverageCents(right) - yieldAverageCents(left);
    if (avgDelta !== 0) return avgDelta;
    const invoiceDelta = asNumber(right.measures?.totalInvoiceCents)
      - asNumber(left.measures?.totalInvoiceCents);
    if (invoiceDelta !== 0) return invoiceDelta;
    return asNumber(right.measures?.callCount) - asNumber(left.measures?.callCount);
  }).slice(0, 3);
}

/**
 * Operator line: name, average, call count. Score is not shown.
 * @param {object} row
 * @returns {string}
 */
export function yieldLine(row) {
  const calls = asNumber(row?.measures?.callCount);
  return `${row?.name || ''} · ${formatWholeDollars(yieldAverageCents(row))} / call · ${calls} calls`;
}

/**
 * @param {object[]} rows
 * @param {{
 *   percentile?: number,
 *   metric?: string,
 *   minCallCount?: number,
 *   direction?: 'bottom'|'top'
 * }} [band]
 * @returns {Set<string>}
 */
export function bottomBandIds(rows, band = {}) {
  const percentile = Number(band.percentile);
  const metric = band.metric || 'totalInvoiceCents';
  const minCallCount = band.minCallCount == null
    ? MIN_CALL_COUNT_DEFAULT
    : Number(band.minCallCount);
  const direction = band.direction === 'top' ? 'top' : 'bottom';
  const pct = Number.isFinite(percentile) && percentile > 0 ? percentile : DEFAULT_BAND_PERCENTILE;
  const eligible = (rows || []).filter((row) => asNumber(row.measures?.callCount) >= minCallCount);
  if (eligible.length === 0) return new Set();
  const sorted = [...eligible].sort((left, right) => (
    asNumber(left.measures?.[metric]) - asNumber(right.measures?.[metric])
  ));
  const count = Math.max(1, Math.ceil(eligible.length * (pct / 100)));
  const slice = direction === 'top' ? sorted.slice(-count) : sorted.slice(0, count);
  return new Set(slice.map((row) => row.id));
}

/**
 * @param {object} row
 * @param {object[]} peers
 * @param {string} [metric]
 * @returns {number|null}
 */
export function rankingPercentile(row, peers, metric = 'totalInvoiceCents') {
  const list = peers || [];
  if (list.length < RANK_PEERS_MIN) return null;
  const value = asNumber(row?.measures?.[metric]);
  const belowOrEqual = list.filter((peer) => asNumber(peer.measures?.[metric]) <= value).length;
  return Math.round((belowOrEqual / list.length) * 100);
}

/**
 * Adjacent stored window before the current grain, if one exists.
 * @param {object} row
 * @param {{ from: Date, to: Date }} range
 * @returns {object|null}
 */
export function adjacentPriorWindow(row, range) {
  const from = range?.from?.getTime?.() ?? new Date(range?.from).getTime();
  if (!Number.isFinite(from)) return null;
  const prior = (row?.windows || [])
    .filter((window) => {
      const end = new Date(window.windowEnd).getTime();
      return Number.isFinite(end) && end <= from;
    })
    .sort((left, right) => new Date(right.windowStart).getTime() - new Date(left.windowStart).getTime());
  return prior[0] || null;
}

/**
 * @param {object} current
 * @param {object|null} prior
 * @param {string} metric
 * @returns {{ absolute: number, percent: number|null }|null}
 */
export function priorPeriodDelta(current, prior, metric) {
  if (!prior) return null;
  const nowValue = asNumber(current?.[metric]);
  const priorValue = asNumber(prior[metric] ?? prior);
  const absolute = nowValue - priorValue;
  const percent = priorValue === 0 ? null : (absolute / Math.abs(priorValue)) * 100;
  return { absolute, percent };
}

/**
 * @param {object} row
 * @param {'invoice'|'calls'} metric
 * @param {string} [timeZone] display zone (company tz, or viewer zone for All)
 * @returns {Array<{ label: string, value: number, bucketStart: string }>}
 */
export function historyStrip(row, metric = 'invoice', timeZone) {
  return [...(row?.windows || [])]
    .sort((left, right) => new Date(left.windowStart).getTime() - new Date(right.windowStart).getTime())
    .map((window) => ({
      label: zonedDateKey(window.windowStart, timeZone) || '',
      value: metric === 'calls'
        ? asNumber(window.callCount)
        : asNumber(window.totalInvoiceCents) / 100,
      bucketStart: window.windowStart
    }));
}

/**
 * @param {object} row
 * @param {object[]} allRows
 * @param {{ from: Date, to: Date }} range
 * @returns {Array<{ companyId: string, companyName: string|null, measures: object }>}
 */
export function perCompanyBreakdown(row, allRows, range) {
  const peers = (allRows || []).filter((candidate) => candidate.peerKey === row.peerKey);
  return peers.map((peer) => {
    const current = (peer.windows || []).filter((window) => windowCountsForRange(window, range));
    return {
      companyId: peer.companyId,
      companyName: peer.companyName || null,
      measures: aggregateWindows(current)
    };
  });
}

/**
 * @param {unknown} createdAt
 * @param {string} [timeZone]
 * @returns {string|null}
 */
export function tenureLine(createdAt, timeZone) {
  if (!createdAt) return null;
  const date = new Date(createdAt);
  if (!Number.isFinite(date.getTime())) return null;
  try {
    const formatted = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      year: 'numeric',
      timeZone: timeZone || undefined
    }).format(date);
    return `Since ${formatted}`;
  } catch {
    return null;
  }
}

/**
 * @param {number|null|undefined} cents
 * @returns {number}
 */
export function centsToDollars(cents) {
  return asNumber(cents) / 100;
}

/**
 * Compact Y-axis money. `$4K`, not `$4,000.00`.
 * @param {number} dollars
 * @returns {string}
 */
export function compactAxisDollars(dollars) {
  const n = asNumber(dollars);
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1000000) {
    const value = abs / 1000000;
    return `${sign}$${value % 1 === 0 ? value : value.toFixed(1)}M`;
  }
  if (abs >= 1000) {
    const rounded = Math.round((abs / 1000) * 10) / 10;
    return `${sign}$${rounded % 1 === 0 ? rounded : rounded.toFixed(1)}K`;
  }
  return `${sign}$${Math.round(abs)}`;
}

/**
 * Whole-number Y-axis counts. `0`, `24`.
 * @param {number} count
 * @returns {string}
 */
export function compactAxisCount(count) {
  return String(Math.round(asNumber(count)));
}

/**
 * Whole-dollar display for callouts and stories.
 * @param {number|null|undefined} cents
 * @returns {string}
 */
export function formatWholeDollars(cents) {
  const dollars = Math.round(centsToDollars(cents));
  const sign = dollars < 0 ? '-' : '';
  return `${sign}$${Math.abs(dollars).toLocaleString('en-US')}`;
}

/**
 * @param {string} filter
 * @returns {{ current: string, prior: string }}
 */
export function timeGrainWords(filter) {
  if (filter === 'hour') return { current: 'last 24 hours', prior: 'the prior 24 hours' };
  if (filter === 'day') return { current: 'last 7 days', prior: 'the prior 7 days' };
  if (filter === 'week') return { current: 'last 8 weeks', prior: 'the prior 8 weeks' };
  if (filter === 'month') return { current: 'this month', prior: 'last month' };
  if (filter === 'quarter') return { current: 'this quarter', prior: 'last quarter' };
  if (filter === 'year') return { current: 'this year', prior: 'last year' };
  return { current: 'today', prior: 'yesterday' };
}

/**
 * Hour, day, and week priors are the same length as the current window.
 * Month uses a full stored prior month. Quarter and year do not.
 *
 * @param {string} [filter]
 * @returns {boolean}
 */
export function sameLengthPrior(filter) {
  return filter === 'hour' || filter === 'day' || filter === 'week';
}

/**
 * Month, quarter, and year priors are not like-for-like. Null always;
 * hour/day/week use timeGrainWords instead.
 *
 * @param {string} [filter]
 * @returns {string|null}
 */
export function storedPeriodVsPhrase(filter) {
  void filter;
  return null;
}

/**
 * Invoice percent vs prior only when both windows are the same length.
 *
 * @param {string} [filter]
 * @returns {boolean}
 */
export function skipUnlikeInvoice(filter) {
  return filter === 'month' || filter === 'quarter' || filter === 'year';
}

/**
 * Last 4 complete weeks plus the current week, or last 4 complete months
 * plus the current month.
 * @param {'weeks4'|'months4'} filter
 * @param {Date} [now]
 * @param {string} [timeZone]
 * @returns {Array<{ from: Date, to: Date, inProgress: boolean }>}
 */
export function modalPeriods(filter, now = new Date(), timeZone) {
  const tz = chartTimeZone(timeZone);
  const periods = [];
  if (filter === MODAL_RANGE.months4) {
    const currentStart = zonedMonthStart(now, tz);
    for (let offset = 4; offset >= 0; offset -= 1) {
      const from = addZonedMonths(currentStart, -offset, tz);
      periods.push({
        from,
        to: addZonedMonths(from, 1, tz),
        inProgress: offset === 0
      });
    }
    return periods;
  }
  const currentStart = zonedWeekStart(now, tz);
  for (let offset = 4; offset >= 0; offset -= 1) {
    const from = addZonedDays(currentStart, -offset * 7, tz);
    periods.push({
      from,
      to: addZonedDays(from, 7, tz),
      inProgress: offset === 0
    });
  }
  return periods;
}

/**
 * Chart range covering the five modal points.
 * @param {'weeks4'|'months4'} filter
 * @param {Date} [now]
 * @param {string} [timeZone]
 * @returns {{ from: Date, to: Date }}
 */
export function modalTimeRange(filter, now = new Date(), timeZone) {
  const periods = modalPeriods(filter, now, timeZone);
  return { from: periods[0].from, to: periods[periods.length - 1].to };
}

/**
 * Same-length range immediately before `range`.
 * @param {{ from: Date, to: Date }} range
 * @returns {{ from: Date, to: Date }|null}
 */
export function priorModalRange(range) {
  const from = range?.from?.getTime?.() ?? new Date(range?.from).getTime();
  const to = range?.to?.getTime?.() ?? new Date(range?.to).getTime();
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;
  const span = to - from;
  return { from: new Date(from - span), to: new Date(from) };
}

/**
 * Cover end for overlap. A month chunk covers [window_start, covered_through).
 * Hour and day use window_end. Missing watermark on a month uses window_end
 * so a migrated chunk does not double-count a tail that starts inside it.
 * @param {object} window
 * @returns {number|null}
 */
function windowCoverEndMs(window) {
  const through = coveredThroughMs(window);
  if (through != null) return through;
  const end = new Date(window?.windowEnd).getTime();
  return Number.isFinite(end) ? end : null;
}

/**
 * Finest non-overlapping series. Overlaps keep the shorter window.
 * Month overlap stops at covered_through so a tail after the watermark
 * is not collapsed into the chunk.
 * @param {object[]} windows
 * @returns {object[]}
 */
export function distinctPeriodWindows(windows) {
  const picked = [];
  preferShorterWindows(windows).forEach((window) => {
    const start = new Date(window.windowStart).getTime();
    const end = windowCoverEndMs(window);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return;
    const overlaps = picked.some((kept) => {
      const keptStart = new Date(kept.windowStart).getTime();
      const keptEnd = windowCoverEndMs(kept);
      return Number.isFinite(keptStart)
        && Number.isFinite(keptEnd)
        && start < keptEnd
        && end > keptStart;
    });
    if (!overlaps) picked.push(window);
  });
  return picked.sort((left, right) => (
    new Date(left.windowStart).getTime() - new Date(right.windowStart).getTime()
  ));
}

/**
 * Sum distinct periods. Recomputes average from the totals.
 * A null totalBalanceCents on any picked period stays null.
 * updatedAt is the newest stamp among the picked periods.
 * @param {object[]} windows
 * @returns {typeof ZERO_MEASURES & { expenses: Record<string, number> }}
 */
export function sumMeasures(windows) {
  const periods = distinctPeriodWindows(windows);
  if (periods.length === 0) {
    return { ...ZERO_MEASURES, expenses: {} };
  }
  const expenses = {};
  let callCount = 0;
  let totalInvoiceCents = 0;
  let totalBalanceCents = 0;
  let balanceMissing = false;
  let totalCommissionsCents = 0;
  let towingSubtotalsCents = 0;
  let storageSubtotalsCents = 0;
  let totalExpensesCents = 0;
  let updatedAt = null;
  periods.forEach((window) => {
    const one = measuresFromWindow(window);
    callCount += one.callCount;
    totalInvoiceCents += one.totalInvoiceCents;
    if (one.totalBalanceCents === null) {
      balanceMissing = true;
    } else if (!balanceMissing) {
      totalBalanceCents += one.totalBalanceCents;
    }
    totalCommissionsCents += one.totalCommissionsCents;
    towingSubtotalsCents += one.towingSubtotalsCents;
    storageSubtotalsCents += one.storageSubtotalsCents;
    totalExpensesCents += one.totalExpensesCents;
    updatedAt = newerStamp(updatedAt, one.updatedAt);
    Object.keys(one.expenses || {}).forEach((key) => {
      expenses[key] = (expenses[key] || 0) + asNumber(one.expenses[key]);
    });
  });
  return {
    callCount,
    totalInvoiceCents,
    totalBalanceCents: balanceMissing ? null : totalBalanceCents,
    totalCommissionsCents,
    averageInvoicePerCallCents: callCount > 0
      ? Math.round(totalInvoiceCents / callCount)
      : 0,
    towingSubtotalsCents,
    storageSubtotalsCents,
    totalExpensesCents,
    expenses,
    updatedAt
  };
}

/**
 * Put a stored window on one period. Prefer the period that contains
 * windowStart so a crossing window is not counted twice.
 * @param {object} window
 * @param {Array<{ from: Date, to: Date }>} periods
 * @returns {{ from: Date, to: Date }|null}
 */
export function assignWindowToPeriod(window, periods) {
  const list = periods || [];
  const start = new Date(window?.windowStart).getTime();
  if (!Number.isFinite(start)) return null;
  return list.find((period) => (
    start >= period.from.getTime() && start < period.to.getTime()
  )) || null;
}

/**
 * Instant for covered_through. A month chunk without a watermark
 * uses window_end so the tail does not double-count the chunk.
 * @param {object} window
 * @returns {number|null}
 */
function coveredThroughMs(window) {
  if (!window) return null;
  if (window.coveredThrough != null) {
    const ms = new Date(window.coveredThrough).getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (storedWindowGrain(window) === 'month') {
    const end = new Date(window.windowEnd).getTime();
    return Number.isFinite(end) ? end : null;
  }
  return null;
}

/**
 * Hours fully tile [start, end) in the sales-org zone. No hardcoded 24.
 * Spring-forward and fall-back days are the stored hour chain.
 * @param {object[]} hours
 * @param {number} startMs
 * @param {number} endMs
 * @returns {boolean}
 */
export function hoursTileInterval(hours, startMs, endMs) {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return false;
  }
  const list = (hours || [])
    .filter((window) => isStoredHourPoint(window))
    .map((window) => ({
      start: new Date(window.windowStart).getTime(),
      end: new Date(window.windowEnd).getTime()
    }))
    .filter((row) => (
      Number.isFinite(row.start)
      && Number.isFinite(row.end)
      && row.start >= startMs
      && row.start < endMs
      && row.end > row.start
    ))
    .sort((left, right) => left.start - right.start);
  if (!list.length) return false;
  let cursor = startMs;
  for (const row of list) {
    if (row.start !== cursor) return false;
    cursor = row.end;
  }
  return cursor === endMs;
}

/**
 * Day row whose windowStart is the current civil day is ignored.
 * Catch-up does not land today. Day Current is today's hours.
 * @param {object} window
 * @param {Date} now
 * @param {string} timeZone
 * @returns {boolean}
 */
function isCurrentCivilDayRow(window, now, timeZone) {
  if (!window || storedWindowGrain(window) !== 'day') return false;
  const tz = chartTimeZone(timeZone);
  const start = new Date(window.windowStart).getTime();
  const today = zonedDayStart(now, tz).getTime();
  const tomorrow = addZonedDays(new Date(today), 1, tz).getTime();
  return start >= today && start < tomorrow;
}

/**
 * In the tail, a day row wins unless hours fully tile that civil day.
 * @param {object[]} windows
 * @param {Date} now
 * @param {string} timeZone
 * @returns {object[]}
 */
function selectTailWindows(windows, now, timeZone) {
  const tz = chartTimeZone(timeZone);
  const usable = (windows || []).filter((window) => (
    !isCurrentCivilDayRow(window, now, tz)
  ));
  const byDay = new Map();
  usable.forEach((window) => {
    const key = zonedDateKey(window.windowStart, tz);
    if (!key) return;
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(window);
  });
  const picked = [];
  byDay.forEach((rows) => {
    const dayRows = rows.filter((window) => storedWindowGrain(window) === 'day');
    const hours = rows.filter((window) => isStoredHourPoint(window));
    const extras = rows.filter((window) => (
      storedWindowGrain(window) !== 'day' && !isStoredHourPoint(window)
    ));
    picked.push(...extras);
    if (!dayRows.length) {
      picked.push(...hours);
      return;
    }
    const dayStart = zonedDayStart(dayRows[0].windowStart, tz);
    const dayEnd = addZonedDays(dayStart, 1, tz);
    if (hoursTileInterval(hours, dayStart.getTime(), dayEnd.getTime())) {
      picked.push(...hours);
      return;
    }
    picked.push(dayRows[0]);
  });
  return picked.sort((left, right) => (
    new Date(left.windowStart).getTime() - new Date(right.windowStart).getTime()
  ));
}

/**
 * Same reportBy + company + name. Selection never collapses two subjects.
 * Stored windows carry companyId (formatWindow). A window without
 * companyId falls back to reportBy + name so a lone unscoped row
 * still groups.
 * @param {object} window
 * @returns {string}
 */
function windowSubjectKey(window) {
  const companyId = trimCell(window?.companyId);
  if (!companyId) {
    return `${trimCell(window?.reportBy)}\0${trimCell(window?.name)}`;
  }
  return `${trimCell(window?.reportBy)}\0${companyId}\0${trimCell(window?.name)}`;
}

/**
 * Month (or week) selection for one subject and one period.
 * @param {object[]} windows
 * @param {{ from: Date, to: Date }} bounds
 * @param {{
 *   filter?: string,
 *   timeZone?: string,
 *   now?: Date,
 *   sibling?: { from: Date, to: Date }|null
 * }} opts
 * @returns {object[]}
 */
function selectCoveredForSubject(windows, bounds, opts) {
  const tz = chartTimeZone(opts.timeZone);
  const now = opts.now instanceof Date ? opts.now : new Date();
  const filter = opts.filter;

  if (!filter || GRAIN_RANK[filter] == null) {
    return distinctPeriodWindows(windows.filter((window) => (
      windowCountsForRange(window, bounds, opts.sibling)
    )));
  }

  if (filter === 'hour' || filter === 'day') {
    const todayStart = zonedDayStart(now, tz);
    const todayEnd = addZonedDays(todayStart, 1, tz);
    const periodIsToday = bounds.from.getTime() === todayStart.getTime()
      && bounds.to.getTime() === todayEnd.getTime();
    return distinctPeriodWindows(windows.filter((window) => (
      windowInLockedRange(window, bounds, filter, opts.sibling)
      && !(periodIsToday && isCurrentCivilDayRow(window, now, tz))
    )));
  }

  if (filter === 'quarter' || filter === 'year') {
    const months = [];
    let cursor = filter === 'year'
      ? zonedYearStart(bounds.from, tz)
      : zonedQuarterStart(bounds.from, tz);
    const last = bounds.to;
    while (cursor < last) {
      const next = addZonedMonths(cursor, 1, tz);
      months.push(...selectCoveredForSubject(windows, { from: cursor, to: next }, {
        ...opts,
        filter: 'month'
      }));
      cursor = next;
      if (months.length > 400) break;
    }
    return months;
  }

  if (filter === 'week') {
    const first = zonedWeekStart(bounds.from, tz);
    const firstEnd = addZonedDays(first, 7, tz);
    if (bounds.to.getTime() > firstEnd.getTime()) {
      const currentWeekStart = zonedWeekStart(now, tz);
      const currentWeekEnd = addZonedDays(currentWeekStart, 7, tz);
      const candidates = windows.filter((window) => (
        storedWindowGrain(window) !== 'month'
        && windowFitsFilter(window, 'week')
        && windowCountsForRange(window, bounds, opts.sibling)
      ));
      const live = now.getTime() >= currentWeekStart.getTime()
        && now.getTime() < currentWeekEnd.getTime()
        && bounds.to.getTime() > currentWeekStart.getTime();
      if (!live) return distinctPeriodWindows(candidates);
      const inCurrent = [];
      const earlier = [];
      candidates.forEach((window) => {
        const start = new Date(window.windowStart).getTime();
        if (start >= currentWeekStart.getTime() && start < currentWeekEnd.getTime()) {
          inCurrent.push(window);
        } else {
          earlier.push(window);
        }
      });
      return [
        ...distinctPeriodWindows(earlier),
        ...selectTailWindows(
          inCurrent.filter((window) => storedWindowGrain(window) !== 'week'),
          now,
          tz
        )
      ];
    }
    const assigned = windows.filter((window) => (
      assignWindowToPeriod(window, [bounds]) === bounds
      || windowBelongsToRange(window, bounds)
    ));
    const finer = assigned.filter((window) => (
      storedWindowGrain(window) !== 'month'
      && (isStoredHourPoint(window)
        || storedWindowGrain(window) === 'day'
        || storedWindowGrain(window) === 'week')
    ));
    const weekRows = finer.filter((window) => storedWindowGrain(window) === 'week');
    const inProgress = now.getTime() >= bounds.from.getTime() && now.getTime() < bounds.to.getTime();
    if (inProgress || weekRows.length === 0) {
      return selectTailWindows(
        finer.filter((window) => storedWindowGrain(window) !== 'week'),
        now,
        tz
      );
    }
    return distinctPeriodWindows(weekRows);
  }

  const assigned = windows.filter((window) => (
    assignWindowToPeriod(window, [bounds]) === bounds
    || windowBelongsToRange(window, bounds)
  ));
  const monthChunk = assigned.find((window) => storedWindowGrain(window) === 'month');
  const coveredMs = monthChunk
    ? coveredThroughMs(monthChunk)
    : bounds.from.getTime();
  const selected = [];
  if (monthChunk) selected.push(monthChunk);
  const tail = assigned.filter((window) => {
    if (window === monthChunk) return false;
    const start = new Date(window.windowStart).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(coveredMs)) return false;
    if (start < coveredMs || start >= bounds.to.getTime()) return false;
    return isStoredHourPoint(window) || storedWindowGrain(window) === 'day';
  });
  selected.push(...selectTailWindows(tail, now, tz));
  return selected;
}

/**
 * One function for the Assets & Crew list, modal Current, and Overview.
 * Groups by subject so two drivers or companies never collapse.
 * @param {object[]} windows
 * @param {{ from: Date, to: Date }} period
 * @param {{
 *   filter?: string,
 *   timeZone?: string,
 *   now?: Date,
 *   sibling?: { from: Date, to: Date }|null
 * }} [opts]
 * @returns {object[]}
 */
export function selectCoveredWindows(windows, period, opts = {}) {
  const bounds = asRange(period);
  if (!bounds) return [];
  const groups = new Map();
  (windows || []).forEach((window) => {
    if (!window) return;
    const key = windowSubjectKey(window);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(window);
  });
  if (groups.size === 0) return [];
  if (groups.size === 1) {
    return selectCoveredForSubject([...groups.values()][0], bounds, opts);
  }
  const picked = [];
  groups.forEach((rows) => {
    picked.push(...selectCoveredForSubject(rows, bounds, opts));
  });
  return picked.sort((left, right) => (
    new Date(left.windowStart).getTime() - new Date(right.windowStart).getTime()
  ));
}

export function measuresInRange(row, range, filter, sibling, opts = {}) {
  const selected = selectCoveredWindows(row?.windows || [], range, {
    filter,
    sibling,
    timeZone: opts.timeZone,
    now: opts.now
  });
  return sumMeasures(selected);
}

/**
 * Unpaid for the selected Time grain. Same period picks as the table
 * (selectCoveredWindows per subject). Sums those picks; does not collapse
 * two subjects that overlap in time. Ignores Show and Ignore low volume.
 * Empty or any null balance is null.
 * @param {object[]} windows
 * @param {{
 *   reportBy?: 'driver'|'truck',
 *   range: { from: Date, to: Date },
 *   filter?: string,
 *   timeZone?: string,
 *   now?: Date
 * }} opts
 * @returns {{ totalBalanceCents: number|null, updatedAt: * }}
 */
export function unpaidCardFromWindows(windows, opts = {}) {
  const reportBy = opts.reportBy;
  const scoped = (Array.isArray(windows) ? windows : []).filter((window) => (
    window && (reportBy == null || window.reportBy === reportBy)
  ));
  const selected = selectCoveredWindows(scoped, opts.range, {
    filter: opts.filter,
    timeZone: opts.timeZone,
    now: opts.now
  });
  if (selected.length === 0) {
    return { totalBalanceCents: null, updatedAt: null };
  }
  let totalBalanceCents = 0;
  let balanceMissing = false;
  let updatedAt = null;
  selected.forEach((window) => {
    const one = measuresFromWindow(window);
    if (one.totalBalanceCents === null) {
      balanceMissing = true;
    } else if (!balanceMissing) {
      totalBalanceCents += one.totalBalanceCents;
    }
    updatedAt = newerStamp(updatedAt, one.updatedAt);
  });
  return {
    totalBalanceCents: balanceMissing ? null : totalBalanceCents,
    updatedAt
  };
}

/**
 * Larger of the two Unpaid sides. A missing side is not the headline.
 * Both null is no data.
 * @param {{ totalBalanceCents: number|null, updatedAt?: * }} driver
 * @param {{ totalBalanceCents: number|null, updatedAt?: * }} truck
 * @returns {{ totalBalanceCents: number|null, updatedAt: *, driverCents: number|null, truckCents: number|null }}
 */
export function pickUnpaidHeadline(driver, truck) {
  const driverCents = driver?.totalBalanceCents;
  const truckCents = truck?.totalBalanceCents;
  const driverAt = driver?.updatedAt || null;
  const truckAt = truck?.updatedAt || null;
  if (driverCents == null && truckCents == null) {
    return {
      totalBalanceCents: null,
      updatedAt: newerStamp(driverAt, truckAt),
      driverCents: null,
      truckCents: null
    };
  }
  if (driverCents == null) {
    return {
      totalBalanceCents: truckCents,
      updatedAt: newerStamp(driverAt, truckAt),
      driverCents: null,
      truckCents
    };
  }
  if (truckCents == null) {
    return {
      totalBalanceCents: driverCents,
      updatedAt: newerStamp(driverAt, truckAt),
      driverCents,
      truckCents: null
    };
  }
  const useTruck = truckCents > driverCents;
  return {
    totalBalanceCents: useTruck ? truckCents : driverCents,
    updatedAt: newerStamp(driverAt, truckAt),
    driverCents,
    truckCents
  };
}

/**
 * @param {unknown} stamp
 * @param {string} [timeZone]
 * @returns {string|null}
 */
function formatNoticeWhen(stamp, timeZone) {
  if (stamp == null || stamp === '') return null;
  const date = stamp instanceof Date ? stamp : new Date(stamp);
  if (Number.isNaN(date.getTime())) return null;
  const parts = zonedDateParts(date, chartTimeZone(timeZone));
  const hour12 = parts.hour % 12 === 0 ? 12 : parts.hour % 12;
  const suffix = parts.hour < 12 ? 'AM' : 'PM';
  const month = MONTH_SHORT[parts.month - 1] || '';
  return `${month} ${parts.day}, ${hour12}:${String(parts.minute).padStart(2, '0')} ${suffix}`;
}

/**
 * @param {unknown} windowStart
 * @param {string} [timeZone]
 * @returns {string}
 */
function formatNoticeHour(windowStart, timeZone) {
  if (windowStart == null) return 'this hour';
  const date = windowStart instanceof Date ? windowStart : new Date(windowStart);
  if (Number.isNaN(date.getTime())) return 'this hour';
  const parts = zonedDateParts(date, chartTimeZone(timeZone));
  const hour12 = parts.hour % 12 === 0 ? 12 : parts.hour % 12;
  const suffix = parts.hour < 12 ? 'AM' : 'PM';
  return `${hour12}:00 ${suffix}`;
}

/**
 * @param {'driver'|'truck'|string|null|undefined} reportBy
 * @returns {string}
 */
function noticeSideLabel(reportBy) {
  if (reportBy === 'driver') return 'Driver';
  if (reportBy === 'truck') return 'Truck';
  return '';
}

function dollarsFromCents(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '$0.00';
  return `$${(n / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
}

/**
 * One hover sentence. Priority: not collecting, failed hour, sides differ.
 * Healthy card returns null.
 * @param {object} input
 * @returns {{ kind: string, sentence: string }|null}
 */
export function formatUnpaidNotice(input = {}) {
  const filter = input.filter;
  const hourDayWeek = filter === 'hour' || filter === 'day' || filter === 'week';
  const collection = input.collection;
  if (collection && collection.kind === 'setup_incomplete') {
    return {
      kind: 'setup_incomplete',
      sentence: "Setup didn't finish. Reconnect in Connectors."
    };
  }
  if (collection && (
    collection.kind === 'paused'
    || collection.kind === 'disconnected'
    || collection.kind === 'auth_failed'
    || collection.kind === 'schedule_paused'
  )) {
    const when = formatNoticeWhen(collection.since, input.timeZone);
    const since = when ? ` since ${when}` : '';
    return {
      kind: collection.kind,
      sentence: `Collecting has been off${since}, so this total is out of date. Turn it back on in Connectors.`
    };
  }
  const failedHours = Array.isArray(input.failedHours) ? input.failedHours : [];
  const failed = failedHours[0];
  if (failed) {
    const side = noticeSideLabel(failed.reportBy);
    const hour = formatNoticeHour(failed.windowStart, input.timeZone);
    const subject = side ? `${side} figures for ${hour}` : `Figures for ${hour}`;
    if (failed.capReached === true) {
      const next = hourDayWeek
        ? 'This hour stays missing here.'
        : 'Month corrects overnight.';
      return {
        kind: 'failed_hour_cap',
        sentence: `${subject} didn't load after 3 tries, so this total is short. ${next}`
      };
    }
    return {
      kind: 'failed_hour',
      sentence: `${subject} didn't load, so this total may be short. We retry on the next hourly pull.`
    };
  }
  const driverCents = input.driverCents;
  const truckCents = input.truckCents;
  if (
    driverCents != null
    && truckCents != null
    && driverCents !== truckCents
  ) {
    const next = hourDayWeek
      ? 'They can stay different here.'
      : 'Month corrects overnight.';
    return {
      kind: 'sides_differ',
      sentence: `By driver ${dollarsFromCents(driverCents)}, by truck ${dollarsFromCents(truckCents)}: a call edited, or missing a driver or truck, after the hour closed. ${next}`
    };
  }
  return null;
}

/**
 * As-of line in the sales-org zone. Date only when the stamp is not today.
 * @param {unknown} updatedAt
 * @param {string} [timeZone]
 * @param {Date} [now]
 * @returns {string|null}
 */
export function formatUnpaidAsOf(updatedAt, timeZone, now) {
  if (updatedAt == null || updatedAt === '') return null;
  const stamp = updatedAt instanceof Date ? updatedAt : new Date(updatedAt);
  if (Number.isNaN(stamp.getTime())) return null;
  const zone = chartTimeZone(timeZone);
  const parts = zonedDateParts(stamp, zone);
  const hour12 = parts.hour % 12 === 0 ? 12 : parts.hour % 12;
  const suffix = parts.hour < 12 ? 'AM' : 'PM';
  const clock = `${hour12}:${String(parts.minute).padStart(2, '0')} ${suffix}`;
  const stampKey = zonedDateKey(stamp, zone);
  const nowKey = zonedDateKey(now || new Date(), zone);
  if (stampKey && nowKey && stampKey === nowKey) {
    return `as of ${clock}`;
  }
  const month = MONTH_SHORT[parts.month - 1] || '';
  return `as of ${month} ${parts.day}, ${clock}`;
}

/**
 * Invoice or call history. Modal Week / Month keeps Current even at zero.
 * Assign by windowStart first. Month Current is the chunk plus the tail.
 * Week Current uses days and hours. Never a month chunk.
 * @param {object} row
 * @param {{ from: Date, to: Date }} range
 * @param {string} [timeZone]
 * @param {'invoice'|'calls'} [metric]
 * @param {{ modalFilter?: 'weeks4'|'months4', now?: Date }} [opts]
 * @returns {Array<{ label: string, value: number, bucketStart: string, measures?: object }>}
 */
export function historyInRange(row, range, timeZone, metric = 'invoice', opts = {}) {
  if (opts.modalFilter === MODAL_RANGE.weeks4 || opts.modalFilter === MODAL_RANGE.months4) {
    const grain = opts.modalFilter === MODAL_RANGE.months4 ? 'month' : 'week';
    const now = opts.now || range?.to || new Date();
    const periods = modalPeriods(opts.modalFilter, now, timeZone);
    const byPeriod = periods.map((period) => ({ period, windows: [] }));
    (row?.windows || []).forEach((window) => {
      const assigned = assignWindowToPeriod(window, periods);
      const bucket = byPeriod.find((entry) => entry.period === assigned);
      if (bucket) bucket.windows.push(window);
    });
    return byPeriod.map((entry) => {
      const windows = selectCoveredWindows(entry.windows, entry.period, {
        filter: grain,
        timeZone,
        now
      });
      const measures = sumMeasures(windows);
      return {
        label: entry.period.inProgress
          ? 'Current'
          : formatFilterBucketLabel(
            grain,
            entry.period.from,
            timeZone
          ),
        value: metric === 'calls'
          ? measures.callCount
          : measures.totalInvoiceCents / 100,
        bucketStart: entry.period.from.toISOString(),
        measures,
        windows,
        inProgress: entry.period.inProgress
      };
    }).filter((point) => point.inProgress || (point.windows || []).length > 0);
  }
  const current = distinctPeriodWindows(
    (row?.windows || []).filter((window) => (
      windowInLockedRange(window, range, opts.filter, opts.sibling)
    ))
  );
  return current.map((window) => ({
    label: zonedDateKey(window.windowStart, timeZone) || '',
    value: metric === 'calls'
      ? asNumber(window.callCount)
      : asNumber(window.totalInvoiceCents) / 100,
    bucketStart: window.windowStart,
    measures: measuresFromWindow(window)
  }));
}

/**
 * Words-only trend when all five points can support it. No paced comparison.
 * @param {Array<{ measures?: object, value?: number }>} points
 * @returns {{ absolute: number, percent: number }|null}
 */
export function modalTrendDelta(points) {
  if (!Array.isArray(points) || points.length !== 5) return null;
  const values = points.map((point) => {
    if (point?.measures) return asNumber(point.measures.totalInvoiceCents);
    return Math.round(asNumber(point?.value) * 100);
  });
  const prior = values.slice(0, 4);
  const current = values[4];
  if (prior.some((value) => value <= 0) || current <= 0) return null;
  const priorAvg = prior.reduce((sum, value) => sum + value, 0) / 4;
  if (priorAvg <= 0) return null;
  const absolute = current - priorAvg;
  return { absolute, percent: (absolute / Math.abs(priorAvg)) * 100 };
}

/**
 * @param {object[]} rows
 * @param {string} [metric]
 * @returns {object[]}
 */
export function sortByMetricDesc(rows, metric = 'totalInvoiceCents') {
  return [...(rows || [])].sort((left, right) => {
    const delta = asNumber(right.measures?.[metric]) - asNumber(left.measures?.[metric]);
    if (delta !== 0) return delta;
    return String(left.name || '').localeCompare(String(right.name || ''));
  });
}

/**
 * How many options that match Show. Top never lists Bottom, and the reverse.
 * @param {string} [mode]
 * @returns {typeof HOW_MANY}
 */
export function howManyOptionsForShow(mode) {
  const direction = mode === SHOW.bottom ? 'bottom' : 'top';
  return HOW_MANY.filter((item) => item.direction === direction);
}

/**
 * @param {object} spec
 * @returns {typeof HOW_MANY[number]}
 */
export function resolveHowMany(spec = {}) {
  const direction = spec.mode === SHOW.bottom ? 'bottom' : 'top';
  const found = HOW_MANY.find((item) => item.key === spec.howMany);
  if (found && found.direction === direction) return found;
  if (found) {
    return HOW_MANY.find((item) => (
      item.direction === direction && item.kind === found.kind && item.n === found.n
    )) || howManyOptionsForShow(direction)[0];
  }
  return howManyOptionsForShow(direction)[0];
}

/**
 * Table filter for Show: All | Top performers | Bottom performers.
 * @param {object[]} rows
 * @param {{
 *   mode?: string,
 *   rankBy?: string,
 *   howMany?: string,
 *   ignoreLowVolume?: boolean,
 *   minCallCount?: number
 * }} [settings]
 * @returns {object[]}
 */
export function applyShowFilter(rows, settings = {}) {
  const mode = settings.mode === SHOW.top || settings.mode === SHOW.bottom
    ? settings.mode
    : SHOW.all;
  if (mode === SHOW.all) return [...(rows || [])];
  const how = resolveHowMany({ ...settings, mode });
  const metric = settings.rankBy || 'totalInvoiceCents';
  let pool = [...(rows || [])];
  if (settings.ignoreLowVolume !== false) {
    const min = settings.minCallCount == null
      ? MIN_CALL_COUNT_DEFAULT
      : Number(settings.minCallCount);
    pool = pool.filter((row) => asNumber(row.measures?.callCount) >= min);
  }
  if (pool.length === 0) return [];
  const sorted = sortByMetricDesc(pool, metric);
  const take = how.kind === 'count'
    ? how.n
    : Math.max(1, Math.ceil(pool.length * (how.n / 100)));
  return mode === SHOW.top
    ? sorted.slice(0, take)
    : sorted.slice(-take);
}

/**
 * @param {object} row
 * @returns {string}
 */
export function subjectLabel(row) {
  if (row?.kind === 'truck' && !row.unassigned && row.name) {
    return `Unit ${row.name}`;
  }
  return row?.name || (row?.kind === 'truck' ? 'This truck' : 'This driver');
}

/**
 * @param {object} row
 * @param {object[]} peers
 * @param {string} [metric]
 * @returns {{ side: 'top'|'bottom', percent: number }|null}
 */
export function rankBand(row, peers, metric = 'totalInvoiceCents') {
  const list = peers || [];
  if (list.length < 2) return null;
  const sorted = sortByMetricDesc(list, metric);
  const index = sorted.findIndex((peer) => peer.id === row?.id);
  if (index < 0) return null;
  const rank = index + 1;
  const fromTop = Math.max(1, Math.round((rank / sorted.length) * 100));
  const fromBottom = Math.max(1, Math.round(((sorted.length - rank + 1) / sorted.length) * 100));
  if (fromTop <= fromBottom) return { side: 'top', percent: fromTop };
  return { side: 'bottom', percent: fromBottom };
}

/**
 * Operator-facing percentile. Never "Ranking percentile 100".
 * @param {object} row
 * @param {object[]} peers
 * @param {string} [metric]
 * @returns {string|null}
 */
export function percentileSentence(row, peers, metric = 'totalInvoiceCents') {
  const band = rankBand(row, peers, metric);
  if (!band) return null;
  const kind = row?.kind === 'truck' ? 'trucks' : 'drivers';
  const rank = RANK_BY.find((item) => item.key === metric) || RANK_BY[0];
  const side = band.side === 'top' ? 'Top' : 'Bottom';
  return `${side} ${band.percent}% of ${kind} by ${rank.label}`;
}

/**
 * Modal delta names the equal-length span it compares, not a single week/month.
 * @param {'weeks4'|'months4'} modalFilter
 * @returns {string}
 */
export function modalPriorSpanPhrase(modalFilter) {
  return modalFilter === MODAL_RANGE.months4
    ? 'over the prior 4 months'
    : 'over the prior 4 weeks';
}

/**
 * @param {{ absolute: number, percent: number|null }|null} delta
 * @param {'weeks4'|'months4'} modalFilter
 * @returns {string|null}
 */
export function deltaSentence(delta, modalFilter) {
  const span = modalFilter === MODAL_RANGE.months4 ? 'vs prior 4 months' : 'vs prior 4 weeks';
  if (!delta) return null;
  if (delta.absolute === 0) return `No change ${span}`;
  const sign = delta.absolute > 0 ? '+' : '−';
  const amount = formatWholeDollars(Math.abs(delta.absolute)).replace('-', '');
  return `${sign}${amount} ${span}`;
}

/**
 * @param {'weeks4'|'months4'} modalFilter
 * @returns {string}
 */
export function modalFilterPhrase(modalFilter) {
  return modalFilter === MODAL_RANGE.months4 ? 'this month' : 'this week';
}

/**
 * @param {object[]} rows
 * @param {string} [metric]
 * @returns {number}
 */
export function peerAverage(rows, metric = 'totalInvoiceCents') {
  const list = rows || [];
  if (list.length === 0) return 0;
  const total = list.reduce((sum, row) => sum + asNumber(row.measures?.[metric]), 0);
  return Math.round(total / list.length);
}

/**
 * One short story under the tiles. Changes with the modal time filter.
 * @param {{
 *   row: object,
 *   measures: object,
 *   peers: object[],
 *   peerMeasures?: object[],
 *   delta: { absolute: number, percent: number|null }|null,
 *   modalFilter: 'weeks4'|'months4'
 * }} input
 * @returns {string}
 */
export function storyText(input) {
  const row = input.row;
  const measures = input.measures || row?.measures || ZERO_MEASURES;
  const peers = input.peers || [];
  const label = subjectLabel(row);
  const kind = row?.kind === 'truck' ? 'trucks' : 'drivers';
  const band = rankBand(
    { ...row, measures },
    (input.peerMeasures || peers).map((peer, index) => (
      peer.measures ? peer : { ...peers[index], measures: peer }
    )),
    'totalInvoiceCents'
  );
  const parts = [];
  if (band) {
    parts.push(
      `${label} is in the ${band.side} ${band.percent}% of ${kind} by Total Invoice.`
    );
  } else {
    parts.push(`${label} has ${asNumber(measures.callCount)} calls ${modalFilterPhrase(input.modalFilter)}.`);
  }
  if (row?.kind === 'truck') {
    const invoice = asNumber(measures.totalInvoiceCents);
    const balance = measures.totalBalanceCents;
    if (invoice > 0 && balance != null) {
      const share = Math.round((asNumber(balance) / invoice) * 100);
      parts.push(`Balance is ${formatWholeDollars(asNumber(balance))}, ${share}% of invoice.`);
    }
  } else {
    const avg = asNumber(measures.averageInvoicePerCallCents);
    const companyAvg = peerAverage(input.peerMeasures || peers, 'averageInvoicePerCallCents');
    if (avg > 0 && companyAvg > 0) {
      const relation = avg < companyAvg ? 'below' : 'above';
      parts.push(
        `Average invoice is ${formatWholeDollars(avg)}, ${relation} the company average of ${formatWholeDollars(companyAvg)}.`
      );
    }
  }
  return parts.join(' ');
}

/**
 * Verdict under the chart. Does not repeat the story.
 * @param {{
 *   row: object,
 *   measures: object,
 *   peers: object[],
 *   peerMeasures?: object[],
 *   modalFilter: 'weeks4'|'months4'
 * }} input
 * @returns {string}
 */
export function valueLine(input) {
  const row = input.row;
  const measures = input.measures || row?.measures || ZERO_MEASURES;
  const peers = input.peerMeasures || input.peers || [];
  const label = subjectLabel(row);
  const calls = asNumber(measures.callCount);
  const invoice = asNumber(measures.totalInvoiceCents);
  const avgInvoice = peerAverage(peers, 'totalInvoiceCents');
  const avgPerCall = peerAverage(peers, 'averageInvoicePerCallCents');
  const band = rankBand({ ...row, measures }, peers, 'totalInvoiceCents');
  const span = modalFilterPhrase(input.modalFilter);
  const isTruck = row?.kind === 'truck';

  const lowestInvoice = (peers || []).length > 0
    && (peers || []).every((peer) => asNumber(peer.measures?.totalInvoiceCents) >= invoice);
  const perCall = asNumber(measures.averageInvoicePerCallCents);
  const perCallWeak = avgPerCall > 0 && perCall < avgPerCall;
  const perCallHealthy = avgPerCall > 0 && perCall >= avgPerCall;

  if (calls > 0 && calls < 5) {
    return isTruck
      ? quietTruckLine(invoice, avgInvoice)
      : `${calls} calls ${span}. Too few to judge, not a performance problem, just not enough work yet.`;
  }
  if (calls === 0) {
    return isTruck
      ? quietTruckLine(invoice, avgInvoice)
      : `Light stretch. Invoice is low because the call count is low. Check again after more calls.`;
  }
  if (isTruck && band?.side === 'bottom' && invoice <= avgInvoice && lowestInvoice) {
    return 'This truck is costing more than it returns. Lowest invoice in the fleet.';
  }
  if (isTruck && invoice < avgInvoice) {
    return `${label} is behind the company average. ${calls} calls and ${formatWholeDollars(invoice)}, not a failure yet, but it is not carrying its share.`;
  }
  if (isTruck && invoice >= avgInvoice) {
    return `${label} is paying for itself. ${formatWholeDollars(invoice)} ${span}, above the company average.`;
  }
  if (calls >= 8 && perCallWeak) {
    return `This driver is working but not earning. Call count is fine. Invoice per call is well under the company.`;
  }
  if (band?.side === 'top' && perCallHealthy) {
    return `Keep this driver. Top ${band.percent}% by invoice and the average per call is healthy.`;
  }
  if (band?.side === 'top') {
    return `Keep this driver. Top ${band.percent}% by invoice.`;
  }
  if (invoice >= avgInvoice && calls >= 8) {
    return `${label} is a high earner. ${formatWholeDollars(invoice)} ${span}, above the company average, and calls are steady.`;
  }
  if (invoice >= avgInvoice) {
    return `${label} is a high earner. ${formatWholeDollars(invoice)} ${span}, above the company average.`;
  }
  if (invoice < avgInvoice && calls >= 8 && perCallWeak) {
    return `Below the company average on invoice, and ${calls} calls means this is not a slow stretch. The average per call is the gap.`;
  }
  if (invoice < avgInvoice && calls >= 8) {
    return `Below the company average on invoice, and ${calls} calls means this is not a slow stretch.`;
  }
  if (calls < 8) {
    return `Light stretch. Invoice is low because the call count is low. Check again after more calls.`;
  }
  return `${label} billed ${formatWholeDollars(invoice)} ${span}.`;
}

/**
 * Quiet-truck verdict. Never "Invoice is calls are light."
 * @param {number} invoice
 * @param {number} avgInvoice
 * @returns {string}
 */
function quietTruckLine(invoice, avgInvoice) {
  if (invoice < avgInvoice) {
    return 'This truck is quiet. Invoice is below the fleet average and calls are light. Worth a look before more work lands.';
  }
  return 'This truck is quiet. Calls are light. Worth a look before more work lands.';
}

/**
 * One revolving line at a time. Names the Time grain. Never says "window."
 * Current and prior are the same range sum the table uses.
 * Skips commission and expense.
 * @param {{
 *   rows: object[],
 *   reportBy: 'driver'|'truck',
 *   filter: string,
 *   currentRange?: { from: Date, to: Date },
 *   previousRange?: { from: Date, to: Date }|null
 * }} input
 * @returns {string[]}
 */
export function revolvingLines(input) {
  const rows = input.rows || [];
  const words = timeGrainWords(input.filter);
  const current = rows.map((row) => ({
    measures: input.currentRange
      ? measuresInRange(row, input.currentRange, input.filter, null, {
        timeZone: input.timeZone,
        now: input.now
      })
      : (row?.measures || { ...ZERO_MEASURES, expenses: {} })
  }));
  const previous = rows.map((row) => (
    input.previousRange
      ? measuresInRange(row, input.previousRange, input.filter, input.currentRange, {
        timeZone: input.timeZone,
        now: input.now
      })
      : { ...ZERO_MEASURES, expenses: {} }
  ));
  const grainRows = rows.map((row, index) => ({ ...row, measures: current[index].measures }));
  const lines = [];
  const invoice = current.reduce((sum, item) => sum + asNumber(item.measures.totalInvoiceCents), 0);
  const calls = current.reduce((sum, item) => sum + asNumber(item.measures.callCount), 0);
  const priorInvoice = previous.reduce((sum, measures) => sum + asNumber(measures.totalInvoiceCents), 0);
  const priorCalls = previous.reduce((sum, measures) => sum + asNumber(measures.callCount), 0);
  const skipInvoice = skipUnlikeInvoice(input.filter)
    || input.invoiceComparable === false;
  const storedVs = storedPeriodVsPhrase(input.filter);
  const invoiceVs = storedVs || `vs ${words.prior}`;
  if (!skipInvoice && priorInvoice > 0) {
    const pct = Math.round(((invoice - priorInvoice) / Math.abs(priorInvoice)) * 100);
    if (pct > 0) lines.push(`Invoice is up ${pct}% ${invoiceVs}.`);
    else if (pct < 0) lines.push(`Invoice is down ${Math.abs(pct)}% ${invoiceVs}.`);
  }
  const leader = topThreeByInvoice(grainRows)[0];
  if (leader && invoice > 0) {
    const share = Math.round((asNumber(leader.measures?.totalInvoiceCents) / invoice) * 100);
    const noun = input.reportBy === 'truck' ? 'truck' : 'driver';
    lines.push(
      `${subjectLabel(leader)} is carrying ${share}% of ${noun} invoice ${words.current}.`
    );
  }
  const zero = current.filter((item) => asNumber(item.measures.callCount) === 0).length;
  if (zero > 0) {
    const noun = input.reportBy === 'truck'
      ? (zero === 1 ? 'truck' : 'trucks')
      : (zero === 1 ? 'driver' : 'drivers');
    lines.push(`${zero} ${noun} ran zero calls ${words.current}.`);
  }
  if (!skipInvoice && calls > 0 && priorCalls > 0 && priorInvoice > 0) {
    const avg = Math.round(invoice / calls);
    const priorAvg = Math.round(priorInvoice / priorCalls);
    if (avg !== priorAvg) {
      const direction = avg < priorAvg ? 'down' : 'up';
      const avgVs = storedVs || words.prior;
      lines.push(
        `Average invoice is ${formatWholeDollars(avg)}, ${direction} from ${formatWholeDollars(priorAvg)} ${avgVs}.`
      );
    }
  }
  return lines.filter((line) => !/window/i.test(line));
}
